/**
 * TrancheBandEditor — a reusable editable progressive-tranche band table (#17).
 *
 * Controlled component: the parent owns `bands: BandDraft[]` and gets `onChange`.
 * Contiguity is DERIVED — each row's lower bound is the previous row's upper
 * bound (0 for the first); the last row is always open-top (∞). The user only
 * sets the upper thresholds + each band's rate, so band sets are always valid by
 * construction. Helpers convert to/from the API's cent/bps shapes.
 *
 * Used by the Agreement Fees tab (Custom authoring, Inc C-2). The firm Standard
 * admin page has its own equivalent editor (Inc A); de-duplicating the two onto
 * this component is a tracked follow-up.
 */
import { ActionIcon, Button, NumberInput, Table, Text, Tooltip } from '@mantine/core';
import { IconPlus, IconTrash } from '@tabler/icons-react';

import {
  bpsToPct, centsToEur, eurToCents, pctToBps,
  type FeeTrancheIn, type FeeTrancheOut,
} from '../api/feeSchedules';

export interface BandDraft { rowKey: string; upTo: number | null; rate: number }

let _bandSeq = 0;
const newKey = () => `band-${_bandSeq++}`;

export const emptyBands = (): BandDraft[] => [{ rowKey: newKey(), upTo: null, rate: 0 }];

/** Map saved tranches → editor rows (€ + %, last = open-top). */
export function bandsFromTranches(tranches: FeeTrancheOut[] | undefined): BandDraft[] {
  if (!tranches || tranches.length === 0) return emptyBands();
  return tranches.slice().sort((a, b) => a.seq - b.seq).map((t) => ({
    rowKey: newKey(),
    upTo: t.max_eur_cents === null ? null : centsToEur(t.max_eur_cents),
    rate: bpsToPct(t.rate_bps),
  }));
}

/** Lower bound (€) of row i = the previous row's upper bound (0 for the first). */
export function bandLowerOf(rows: BandDraft[], i: number): number {
  return i === 0 ? 0 : (rows[i - 1].upTo ?? 0);
}

/** Validate the draft rows; returns an error message or null. */
export function validateBandDrafts(rows: BandDraft[]): string | null {
  if (rows.length === 0) return 'At least one band is required.';
  for (let i = 0; i < rows.length - 1; i++) {
    const lo = bandLowerOf(rows, i);
    const up = rows[i].upTo;
    if (up === null || up === undefined || Number.isNaN(up)) return `Band ${i + 1} needs an upper threshold (€).`;
    if (up <= lo) return `Band ${i + 1}: "up to" (€${up.toLocaleString()}) must be greater than €${lo.toLocaleString()}.`;
  }
  for (const b of rows) if (b.rate < 0 || b.rate > 100) return 'Each rate must be between 0 and 100 %.';
  return null;
}

/** Convert draft rows → API FeeTrancheIn[] (cents/bps; last band open-top). */
export function buildBandsIn(rows: BandDraft[]): FeeTrancheIn[] {
  return rows.map((b, i) => ({
    min_eur_cents: eurToCents(bandLowerOf(rows, i)),
    max_eur_cents: i === rows.length - 1 ? null : eurToCents(b.upTo as number),
    rate_bps: pctToBps(b.rate),
  }));
}

export function TrancheBandEditor({ bands, onChange }: {
  bands: BandDraft[];
  onChange: (b: BandDraft[]) => void;
}) {
  const patch = (k: string, p: Partial<BandDraft>) =>
    onChange(bands.map((r) => (r.rowKey === k ? { ...r, ...p } : r)));
  const addBand = () => onChange([...bands, { rowKey: newKey(), upTo: null, rate: 0 }]);
  const removeBand = (k: string) => {
    if (bands.length <= 1) return;
    const filtered = bands.filter((r) => r.rowKey !== k);
    onChange(filtered.map((r, idx) => (idx === filtered.length - 1 ? { ...r, upTo: null } : r)));
  };

  return (
    <Table withTableBorder>
      <Table.Thead><Table.Tr>
        <Table.Th miw={210}>Band (AuM)</Table.Th>
        <Table.Th w={190}>Up to (€)</Table.Th>
        <Table.Th w={130}>Rate (%/yr)</Table.Th>
        <Table.Th w={48} />
      </Table.Tr></Table.Thead>
      <Table.Tbody>
        {bands.map((r, i, arr) => {
          const lo = bandLowerOf(arr, i);
          const isLast = i === arr.length - 1;
          return (
            <Table.Tr key={r.rowKey}>
              <Table.Td>
                <Text size="sm" style={{ whiteSpace: 'nowrap' }}>
                  €{lo.toLocaleString()} {isLast ? 'and above' : `– €${(r.upTo ?? 0).toLocaleString()}`}
                </Text>
              </Table.Td>
              <Table.Td>
                {!isLast
                  ? <NumberInput size="xs" min={0} thousandSeparator value={r.upTo ?? ''}
                      onChange={(v) => patch(r.rowKey, { upTo: typeof v === 'number' ? v : null })} />
                  : <Text c="dimmed">∞</Text>}
              </Table.Td>
              <Table.Td>
                <NumberInput size="xs" min={0} max={100} step={0.01} decimalScale={2} suffix=" %"
                  value={r.rate} onChange={(v) => patch(r.rowKey, { rate: typeof v === 'number' ? v : 0 })} />
              </Table.Td>
              <Table.Td>
                <Tooltip label={arr.length <= 1 ? 'At least one band is required' : 'Remove band'} withArrow>
                  <ActionIcon variant="subtle" color="red" disabled={arr.length <= 1}
                    onClick={() => removeBand(r.rowKey)} aria-label="Remove band">
                    <IconTrash size={16} />
                  </ActionIcon>
                </Tooltip>
              </Table.Td>
            </Table.Tr>
          );
        })}
      </Table.Tbody>
      <Table.Tfoot><Table.Tr>
        <Table.Td colSpan={4}>
          <Button size="xs" variant="light" leftSection={<IconPlus size={14} />} onClick={addBand}>
            Add band
          </Button>
        </Table.Td>
      </Table.Tr></Table.Tfoot>
    </Table>
  );
}
